import React, { useState } from "react";
import NumberFormat from "react-number-format";

import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { Autocomplete, Stack } from "@mui/material";
import TextField from "@mui/material/TextField";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import dayjs from "dayjs";
import { useDateFormatSelect } from "store";
import { QuickViewMeasureData } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { currenciesArray, currencySign } from "../../../../constants";
import { UpdateMeasureData } from "./EditTableCell";

interface Props {
  data: QuickViewMeasureData;
  updateMeasureData: UpdateMeasureData;
  deleteMeasureData: (measure_data_id: number) => void;
  isAbsolute?: boolean;
  isNegative?: boolean;
  isCurrency?: boolean;
}

export const MeasureDataItem: React.FC<Props> = ({
  data,
  updateMeasureData,
  deleteMeasureData,
  isAbsolute,
  isNegative,
  isCurrency
}) => {
  const dateFormat = useDateFormatSelect();

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [allowNegative, setAllowNegative] = useState<boolean>(true);
  const [commentOpen, setCommentOpen] = useState<boolean>(false);

  return (
    <Stack
      sx={{
        borderBottom: "1px solid #dae0e2",
        p: "5px 0 5px 16px",
        ".hidable-icon": {
          transition: "opacity 0.3s ease-in-out"
        },
        ":hover": {
          ".hidable-icon": { opacity: 1 }
        }
      }}
    >
      <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "10px" }}>
        <DesktopDatePicker
          open={openDatePicker}
          onClose={() => setOpenDatePicker(false)}
          format={dateFormat}
          views={["year", "month", "day"]}
          slotProps={{
            popper: {
              sx: {
                minWidth: "335px",
                ".MuiPickersCalendarHeader-labelContainer": {
                  fontSize: "0.9rem"
                }
              }
            },
            textField: {
              InputProps: {
                readOnly: true
              },
              // onKeyDown: (e) => e.preventDefault(),
              error: false,
              onClick: () => setOpenDatePicker(true),
              sx: {
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none"
                },
                "& .MuiOutlinedInput-root": { padding: "0" },
                "& .MuiOutlinedInput-input": {
                  cursor: "pointer",
                  border: "none",
                  padding: 0,
                  fontSize: "16px",
                  fontWeight: 500,
                  width: "103px",
                  height: "26px",
                  textAlign: "center",
                  color: "#0a3142",
                  marginRight: "5px"
                },
                "& .MuiIconButton-root": { padding: 0, display: "none" },
                "& .MuiSvgIcon-root": { width: "15px", display: "none" },
                "& .MuiInputAdornment-root": { width: "0px", display: "none" }
              }
            }
          }}
          value={dayjs(data.date)}
          onChange={(date) => {
            if (!date || !date.isValid()) return;
            // setOldDateValue(data.date);
            updateMeasureData({
              measure_data_id: data.measure_data_id,
              date: toIsoMidday(date.toISOString())
            });
          }}
        />
        <NumberFormat
          customInput={TextField}
          maxLength={11}
          allowNegative={allowNegative}
          thousandSeparator
          isNumericString
          sx={{
            ":hover": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: "inherit",
                borderStyle: "solid",
                borderWidth: "1px"
              }
            },
            "& .MuiOutlinedInput-input": {
              textAlign: "left",
              height: "26px",
              width: "100px",
              padding: "0 5px",
              fontSize: "15px"
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none"
            }
          }}
          value={data.value}
          onValueChange={({ value }) => {
            updateMeasureData({
              measure_data_id: data.measure_data_id,
              value: Number(value)
            });
          }}
          onFocus={(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
            if (!e) return;
            if (isAbsolute) {
              //if value absolute it means it can't contain minus sign => so prop allowNegative should be changed to false
              setAllowNegative(false);
            } else {
              // the opposite thing, if value not absolute, in can contain minus, so it can be any value (user enter what he want)
              setAllowNegative(true);
            }
            if (isNegative) {
              if (data.edited) return; //this value can be changed to opposite only one time, to prevent user from repeating of reversing value we added flag to hint us that value inside input was edited, so if it was edited we can't multiply by "-1" it again!!!
              //we need handling case if measure is_negative: true in moment when user click to input
              updateMeasureData({
                measure_data_id: data.measure_data_id,
                edited: true,
                value: isAbsolute
                  ? Math.abs(Number(e.target.value.replace(/,/g, ""))) // if measure is_absolute: true, we need to take Math.abs from this value, it means that value can be positive only
                  : Number(e.target.value.replace(/,/g, "")) * -1 // if measure is_absolute: false && is_negative: true, we need multiply the value by -1, so if value was positive after multiplying it should be negative (and opposite case)
              });
            }
          }}
        />
        {isCurrency && (
          <Autocomplete
            options={currenciesArray}
            value={data.currency}
            defaultValue={data.currency}
            onChange={(_, option) => {
              if (!option) return;
              updateMeasureData({
                measure_data_id: data.measure_data_id,
                currency: option,
                postfix: currencySign[option]
              });
            }}
            disableClearable
            renderInput={(params) => (
              <TextField
                sx={{
                  width: "62px",
                  height: "20px",
                  padding: 0,
                  ":hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "inherit",
                      borderStyle: "solid",
                      borderWidth: "1px"
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      display: "block",
                      right: "0!important"
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#5d7782"
                    }
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiSvgIcon-root": {
                    color: "#5d7782"
                  },

                  "& .MuiOutlinedInput-root": {
                    fontSize: "15px",
                    padding: "0 !important"
                  },
                  "& .MuiAutocomplete-endAdornment": {
                    right: "0 !important"
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#fff"
                  },

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#fff"
                  },
                  "& .MuiAutocomplete-popupIndicator:hover": {
                    backgroundColor: "transparent"
                  }
                }}
                {...params}
              />
            )}
          />
        )}
        <Stack
          sx={{ flexDirection: "row", alignItems: "center", gap: "10px", ...(!isCurrency && { paddingLeft: "30px" }) }}
        >
          <MaterialTooltip isShow placement="top" title={data.description}>
            <ChatOutlinedIcon
              className="hidable-icon"
              sx={{ fontSize: "16px", cursor: "pointer", ...(!data.description && { opacity: 0 }) }}
              onClick={() => setCommentOpen(!commentOpen)}
            />
          </MaterialTooltip>
          <CloseOutlinedIcon
            className="hidable-icon"
            sx={{ fontSize: "16px", cursor: "pointer", opacity: 0 }}
            onClick={() => deleteMeasureData(data.measure_data_id)}
          />
        </Stack>
      </Stack>
      {commentOpen && (
        <TextField
          value={data.description}
          onChange={(e) => {
            if (!e) return;
            updateMeasureData({
              measure_data_id: data.measure_data_id,
              description: e.target.value
            });
          }}
          sx={{
            marginTop: "10px",
            marginBottom: "5px",
            marginLeft: "10px",
            width: "96%",
            "& .MuiOutlinedInput-input": { height: "26px", width: "100%", padding: "0 10px" }
          }}
        />
      )}
    </Stack>
  );
};
